/**
 * Lightweight crossfade media slider for the Image + Accordion section's media
 * card. Slides are stacked (CSS opacity crossfade) — no layout reflow.
 *
 * Performance-first: autoplay is gated by an IntersectionObserver (the timer
 * only runs while the slider is on screen and visible — paused off-screen, in
 * an inactive tab panel, when an accordion swaps the media, or when the browser
 * tab is backgrounded). Honors prefers-reduced-motion (no autoplay, no
 * transition). Slides beyond the first are CFH-lazy with data-lazy-keep; the
 * current + next slide are loaded on demand via the observer-CFH event.
 *
 * Markup is fully server-rendered (slides + bullets) — JS only wires
 * interaction and autoplay, so it degrades gracefully without JS.
 *
 * @example
 * <ax-media-slider data-autoplay="true" data-interval="4">
 *   <div class="ax-media-slider__track" ref="track">
 *     <div class="ax-media-slider__slide is-active">…picture…</div>
 *     <div class="ax-media-slider__slide" data-lazy-keep>…picture…</div>
 *   </div>
 *   <div class="ax-media-slider__bullets" ref="bullets">
 *     <button class="ax-media-slider__bullet" aria-current="true">…</button>
 *     <button class="ax-media-slider__bullet">…</button>
 *   </div>
 * </ax-media-slider>
 *
 * @class
 * @extends HTMLElement
 */
class AxMediaSlider extends HTMLElement {
  #selectors = {
    slide: '.ax-media-slider__slide',
    bullet: '.ax-media-slider__bullet',
  };

  #classes = {
    active: 'is-active',
  };

  /** @type {{ slides: HTMLElement[], bullets: HTMLButtonElement[] }} */
  #elements = { slides: [], bullets: [] };

  /** @type {number} */
  #current = 0;

  /** @type {number|null} */
  #timer = null;

  /** @type {IntersectionObserver|null} */
  #observer = null;

  /** @type {boolean} */
  #onScreen = false;

  /** @type {boolean} */
  #hovered = false;

  /** @type {boolean} */
  #autoplay = false;

  /** @type {number} */
  #interval = 4000;

  /** @type {boolean} */
  #reducedMotion = false;

  /**
   * Lifecycle — component connected to DOM.
   */
  connectedCallback() {
    this.#cacheElements();
    if (!this.#isValidSetup()) return;

    this.#autoplay = this.dataset.autoplay !== 'false';
    // data-interval is expressed in seconds (merchant-facing); clamp to ≥2s.
    this.#interval = Math.max(2000, (parseFloat(this.dataset.interval) || 4) * 1000);
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#current = this.#elements.slides.findIndex((slide) =>
      slide.classList.contains(this.#classes.active)
    );
    if (this.#current < 0) this.#current = 0;

    this.#attachEventListeners();
    this.#loadAround(this.#current);
    this.#observeVisibility();
  }

  /**
   * Lifecycle — component removed from DOM.
   */
  disconnectedCallback() {
    this.#stop();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#removeEventListeners();
  }

  /**
   * Cache DOM elements.
   * @private
   */
  #cacheElements() {
    this.#elements = {
      slides: [...this.querySelectorAll(this.#selectors.slide)],
      bullets: [...this.querySelectorAll(this.#selectors.bullet)],
    };
  }

  /**
   * Validate the slider has at least two slides (otherwise it is a static image).
   * @private
   * @returns {boolean}
   */
  #isValidSetup() {
    return this.#elements.slides.length > 1;
  }

  /**
   * Attach event listeners.
   * @private
   */
  #attachEventListeners() {
    this.#elements.bullets.forEach((bullet, index) => {
      bullet.addEventListener('click', () => this.#handleBulletClick(index));
    });
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    this.addEventListener('pointerleave', this.#handlePointerLeave);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /**
   * Remove event listeners.
   * @private
   */
  #removeEventListeners() {
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /**
   * Start/stop autoplay based on the slider's on-screen visibility.
   * @private
   */
  #observeVisibility() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#onScreen = entry.isIntersecting;
          this.#onScreen ? this.#play() : this.#stop();
        }
      },
      { threshold: 0.25 }
    );
    this.#observer.observe(this);
  }

  /**
   * @private
   * @param {number} index
   */
  #handleBulletClick = (index) => {
    this.#goTo(index);
    this.#play();
  };

  /** @private */
  #handlePointerEnter = () => {
    this.#hovered = true;
    this.#stop();
  };

  /** @private */
  #handlePointerLeave = () => {
    this.#hovered = false;
    this.#play();
  };

  /** @private */
  #handleVisibilityChange = () => {
    document.hidden ? this.#stop() : this.#play();
  };

  /**
   * Move to a given slide index (wraps around).
   * @private
   * @param {number} index
   */
  #goTo(index) {
    const { slides, bullets } = this.#elements;
    const next = (index + slides.length) % slides.length;
    if (next === this.#current) return;

    slides[this.#current]?.classList.remove(this.#classes.active);
    bullets[this.#current]?.removeAttribute('aria-current');

    this.#current = next;

    slides[next]?.classList.add(this.#classes.active);
    bullets[next]?.setAttribute('aria-current', 'true');

    this.#loadAround(next);
  }

  /**
   * Trigger CFH lazy-load on the current and next slide.
   * @private
   * @param {number} index
   */
  #loadAround(index) {
    const { slides } = this.#elements;
    for (const offset of [0, 1]) {
      const slide = slides[(index + offset) % slides.length];
      if (slide?.querySelector('[data-lazy-keep]') || slide?.hasAttribute('data-lazy-keep')) {
        document.dispatchEvent(
          new CustomEvent('removeLazyKeep', { detail: { lazyParentElement: slide } })
        );
      }
    }
  }

  /**
   * Start the autoplay timer when every condition allows it.
   * @private
   */
  #play() {
    this.#stop();
    if (!this.#autoplay || this.#reducedMotion) return;
    if (!this.#onScreen || this.#hovered || document.hidden) return;
    if (this.#elements.slides.length < 2) return;

    this.#timer = window.setInterval(() => this.#goTo(this.#current + 1), this.#interval);
  }

  /**
   * Stop the autoplay timer.
   * @private
   */
  #stop() {
    if (this.#timer === null) return;
    window.clearInterval(this.#timer);
    this.#timer = null;
  }
}

customElements.define('ax-media-slider', AxMediaSlider);
